import request from '@/plugins/axios'

/**
 * 证件照与景点合成 API 封装
 *
 * <p>所有接口的用户图片均以 FormData 提交，后端内存处理后字节流返回。
 * matting/addBackground/scenicComposite 走 blob 以直接展示/下载。</p>
 */

/**
 * 人像抠图（返回透明 PNG Blob）
 *
 * @param file 上传的人像图片
 * @return Promise<Blob>
 */
export function matting(file) {
  const formData = new FormData()
  formData.append('inputImage', file)
  return request({
    url: 'photo/matting',
    method: 'POST',
    data: formData,
    responseType: 'blob',
    timeout: 60000
  })
}

/**
 * 证件照制作（返回标准版与高清版 Base64）
 *
 * @param file 上传的证件照来源图片
 * @param options 尺寸与模型参数
 * @return Promise<Result<IdPhotoResultVO>>
 */
export function idphoto(file, options = {}) {
  const formData = new FormData()
  formData.append('inputImage', file)
  if (options.height) formData.append('height', options.height)
  if (options.width) formData.append('width', options.width)
  if (options.humanMattingModel) formData.append('humanMattingModel', options.humanMattingModel)
  if (options.faceDetectModel) formData.append('faceDetectModel', options.faceDetectModel)
  return request({
    url: 'photo/idphoto',
    method: 'POST',
    data: formData,
    timeout: 60000
  })
}

/**
 * 为透明证照添加底色（返回带底色证照 Blob）
 *
 * @param file 透明底证件照 PNG
 * @param color 6 位十六进制底色
 * @param background 可选自定义背景图
 * @return Promise<Blob>
 */
export function addBackground(file, color, background = null) {
  const formData = new FormData()
  formData.append('inputImage', file)
  formData.append('color', color)
  if (background) formData.append('background', background)
  return request({
    url: 'photo/addBackground',
    method: 'POST',
    data: formData,
    responseType: 'blob',
    timeout: 60000
  })
}

/**
 * 查询可用的景点模板
 *
 * @return Promise<Result<Array<ScenicSpotVO>>>
 */
export function getScenicList() {
  return request({
    url: 'photo/scenic/list',
    method: 'GET'
  })
}

/**
 * 景点照片合成（返回合成图 JPEG Blob）
 *
 * @param params 合并参数：人像、背景、归一化坐标与缩放
 * @return Promise<Blob>
 */
export function scenicComposite({ person, background, scenicId, x, y, scale }) {
  const formData = new FormData()
  formData.append('person', person)
  if (background) formData.append('background', background)
  if (scenicId) formData.append('scenicId', scenicId)
  if (x != null) formData.append('x', x)
  if (y != null) formData.append('y', y)
  if (scale != null) formData.append('scale', scale)
  return request({
    url: 'photo/scenic/composite',
    method: 'POST',
    data: formData,
    responseType: 'blob'
  })
}

/**
 * 将 Blob 转成可展示的对象 URL
 *
 * @param blob 图片 Blob
 * @return String 对象 URL
 */
export function blobToUrl(blob) {
  return window.URL.createObjectURL(blob)
}

/**
 * 触发浏览器下载
 *
 * @param url 目标 URL（对象 URL 或 http url）
 * @param filename 保存文件名
 */
export function downloadUrl(url, filename) {
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * 释放对象 URL
 *
 * @param url 对象 URL
 */
export function revokeUrl(url) {
  if (url) {
    window.URL.revokeObjectURL(url)
  }
}